import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@trackman/web-shared-components';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { AnyObject } from '@trackman/web-shared-components/components/types';
import { Column } from 'constants/Column';
import { ISort } from 'constants/ISort';
import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  StickyColumnBackgroundCover,
  StyledTable,
  StyledTableBodyCell,
  StyledTableBodyRow,
  StyledTableFooter,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableHeadRow,
  SubHeader,
  TableContainerWrapper,
  TableHeaderCell,
} from './styles/SimpleTableStyles';

type IProps = {
  columns: Column[];
  data: AnyObject[];
  onHeaderClick?: (columnKey: string, defaultClick?: () => void) => void;
  sort?: ISort;
  isLoading?: boolean;
  noDataComponent?: React.ReactNode;
  width?: string;
  height?: string;
  maxHeight?: string;
  className?: string;
  innerRef?: React.RefObject<HTMLDivElement>;
  variant?: 'default' | 'sticky';
  stickyHeader?: boolean;
} & (
  | {
      withFooter: true;
      page: number;
      count: number;
      rowsPerPage: number;
      onChangePage: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => void;
      onRowsPerPageChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
    }
  | {
      withFooter: false;
      page?: never;
      count?: never;
      rowsPerPage?: never;
      onChangePage?: () => never;
      onRowsPerPageChange?: () => never;
    }
);

const SimpleTable: React.FC<IProps> = observer((props) => {
  const {
    columns,
    data,
    page,
    onChangePage,
    onHeaderClick,
    sort,
    isLoading,
    onRowsPerPageChange,
    rowsPerPage,
    count,
    noDataComponent,
    withFooter,
    width,
    className,
    height,
    maxHeight,
    innerRef,
    variant,
    stickyHeader = false,
  } = props;
  return (
    <TableContainer
      ref={innerRef}
      sx={{
        maxWidth: 1200,
      }}
      style={{
        width: width,
        height: height,
        maxHeight: maxHeight,
        border: 'none',
        overflowY: variant === 'sticky' ? 'hidden' : 'unset',
      }}
      className={className}
    >
      <StyledTable>
        <StyledTableHead stickyHeader={stickyHeader}>
          <StyledTableHeadRow>
            {columns
              .filter((column) => !column.hidden)
              .map((column, index) => {
                return (
                  <StyledTableHeadCell key={index} style={{ fontSize: '13px', minWidth: column.width + 'px' }} width={column.width}>
                    {column.sortable ? (
                      <TableSortLabel
                        sx={{
                          width: 20,
                        }}
                        onClick={() => {
                          if (column.sortable && onHeaderClick) {
                            onHeaderClick(column.accessor as any);
                          }
                        }}
                        active={column.accessor === sort?.column}
                        direction={sort?.direction || undefined}
                      >
                        {column.Header}
                      </TableSortLabel>
                    ) : (
                      <TableHeaderCell>
                        <div>{column.Header}</div>
                        <SubHeader>{column.SubHeader}</SubHeader>
                      </TableHeaderCell>
                    )}
                  </StyledTableHeadCell>
                );
              })}
          </StyledTableHeadRow>
        </StyledTableHead>

        <TableBody>
          {isLoading ? (
            Array.from({ length: 8 }).map(() => {
              return (
                <StyledTableBodyRow>
                  {columns
                    .filter((column) => !column.hidden)
                    .map(() => {
                      return (
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                      );
                    })}
                </StyledTableBodyRow>
              );
            })
          ) : data.length > 0 ? (
            data.map((row, index) => {
              return (
                <StyledTableBodyRow key={index} hover data-testid={`table-row-item-${index}`} variant={variant}>
                  {columns
                    .filter((column) => !column.hidden)
                    .map((column, index) => {
                      return (
                        <StyledTableBodyCell key={index} style={{ fontSize: '13px', minWidth: column.width + 'px' }} width={column.width}>
                          {row[column.accessor as string]
                            ? column.Cell
                              ? column.Cell(row, column.accessor)
                              : row[column.accessor as string]
                            : null}
                        </StyledTableBodyCell>
                      );
                    })}
                </StyledTableBodyRow>
              );
            })
          ) : (
            <TableRow>
              <StyledTableBodyCell colSpan={columns.filter((column) => !column.hidden).length} className='no-data'>
                {noDataComponent}
              </StyledTableBodyCell>
            </TableRow>
          )}
        </TableBody>
        {variant === 'sticky' && <StickyColumnBackgroundCover />}
        {withFooter && data.length > 0 && (
          <StyledTableFooter>
            <TableRow>
              <TablePagination
                count={count}
                onPageChange={onChangePage}
                onRowsPerPageChange={onRowsPerPageChange}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                // @ts-ignore
                SelectProps={{ 'data-testid': 'rows-per-page-select' }}
                // @ts-ignore
                pageInputProps={{ 'data-testid': 'page-input' }}
              />
            </TableRow>
          </StyledTableFooter>
        )}
      </StyledTable>
    </TableContainer>
  );
});

export default SimpleTable;
